import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { ConfigProvider } from 'antd';
import { KANAGAWA_CHART_COLORS_DRAGON, KANAGAWA_CHART_COLORS_LOTUS, KANAGAWA_DRAGON, KANAGAWA_LOTUS, } from './kanagawa';
import { AID_SCROLLBAR_CSS_VARIABLES, buildKanagawaScrollbarTokens, buildKanagawaTheme, } from './theme';
const KanagawaThemeContext = createContext(null);
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';
const MODE_STORAGE_KEY = 'colorMode';
function getStoredMode() {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : null;
}
function getSystemMode() {
    return window.matchMedia(DARK_MODE_QUERY).matches ? 'dark' : 'light';
}
function getInitialMode() {
    return getStoredMode() ?? getSystemMode();
}
export function KanagawaThemeProvider({ children }) {
    const [mode, setModeState] = useState(getInitialMode);
    // Persist manual choice. The index.html anti-blink script reads the same
    // key so a page load paints the selected palette before React mounts.
    useEffect(() => {
        window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    }, [mode]);
    // Follow the system preference only while the user has NOT chosen a mode
    // manually. Once they toggle, the stored choice wins.
    useEffect(() => {
        const mediaQuery = window.matchMedia(DARK_MODE_QUERY);
        const handleChange = (event) => {
            if (getStoredMode() === null) {
                setModeState(event.matches ? 'dark' : 'light');
            }
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);
    const setMode = useCallback((next) => {
        setModeState(next);
    }, []);
    const toggleMode = useCallback(() => {
        setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);
    const themeConfig = useMemo(() => buildKanagawaTheme(mode), [mode]);
    const palette = useMemo(() => (mode === 'dark' ? KANAGAWA_DRAGON : KANAGAWA_LOTUS), [mode]);
    const scrollbarTokens = useMemo(() => buildKanagawaScrollbarTokens(mode), [mode]);
    // Paint document surfaces and native scrollbar variables before the browser
    // paints the committed mode. The index.html anti-blink script still covers
    // the initial background before React mounts; this keeps runtime mode
    // changes and modal/table scrollbars in sync with the same palette.
    useLayoutEffect(() => {
        const root = document.documentElement;
        root.style.backgroundColor = palette.base;
        document.body.style.backgroundColor = palette.base;
        root.style.setProperty(AID_SCROLLBAR_CSS_VARIABLES.track, scrollbarTokens.track);
        root.style.setProperty(AID_SCROLLBAR_CSS_VARIABLES.thumb, scrollbarTokens.thumb);
        root.style.setProperty(AID_SCROLLBAR_CSS_VARIABLES.thumbHover, scrollbarTokens.thumbHover);
    }, [palette, scrollbarTokens]);
    const chartColors = useMemo(() => (mode === 'dark' ? KANAGAWA_CHART_COLORS_DRAGON : KANAGAWA_CHART_COLORS_LOTUS), [mode]);
    const value = useMemo(() => ({ mode, palette, chartColors, setMode, toggleMode }), [mode, palette, chartColors, setMode, toggleMode]);
    return (_jsx(KanagawaThemeContext.Provider, { value: value, children: _jsx(ConfigProvider, { theme: themeConfig, children: children }) }));
}
function useKanagawaThemeContext() {
    const context = useContext(KanagawaThemeContext);
    if (!context) {
        throw new Error('Kanagawa theme hooks must be used within a KanagawaThemeProvider');
    }
    return context;
}
export function useKanagawaMode() {
    return useKanagawaThemeContext().mode;
}
export function useKanagawaSetMode() {
    return useKanagawaThemeContext().setMode;
}
export function useKanagawaToggleMode() {
    return useKanagawaThemeContext().toggleMode;
}
export function useKanagawaPalette() {
    return useKanagawaThemeContext().palette;
}
export function useKanagawaChartColors() {
    return useKanagawaThemeContext().chartColors;
}
//# sourceMappingURL=provider.js.map